import { createPortal } from "react-dom";
import React, { useState, useEffect } from "react";
import { motion as Motion, AnimatePresence } from "framer-motion";
import { useCart } from "@/contexts/CartContext";
import CartHeader from "./CartHeader";
import CartTickets from "./CartTickets";
import CartSummary from "./CartSummary";
import CartsList from "./CartsList";

export default function CartModal({ isOpen, onClose, carts = [], onOpenPayment, showError }) {
  const { selectedEventoId } = useCart();
  const [selectedCart, setSelectedCart] = useState(null);
  const [currentView, setCurrentView] = useState('list'); // 'list' or 'cart'
  const [isUpdatingCart, setIsUpdatingCart] = useState(false); // Estado compartido para bloquear acciones durante actualizaciones

  // Reset view when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (carts.length === 1) {
        setSelectedCart(carts[0]);
        setCurrentView('cart');
      } else if (carts.length > 1) {
        // Si hay un eventoId seleccionado, preseleccionar ese carrito
        if (selectedEventoId) {
          const eventCart = carts.find(c => c.eventoId === selectedEventoId || c.eventoUid === selectedEventoId);
          if (eventCart) {
            setSelectedCart(eventCart);
            setCurrentView('cart');
          } else {
            // Si no se encuentra el carrito del evento, mostrar listado
            setSelectedCart(null);
            setCurrentView('list');
          }
        } else if (selectedCart && currentView === 'cart') {
          // Si ya hay un carrito seleccionado, mantenerlo
          // Buscar el carrito actualizado por su carritoUid
          const updatedCart = carts.find(c => c.carritoUid === selectedCart.carritoUid);
          if (updatedCart) {
            setSelectedCart(updatedCart);
          } else {
            // Si el carrito ya no existe (fue eliminado), volver al listado
            setSelectedCart(null);
            setCurrentView('list');
          }
        } else {
          // Primera vez que se abre con múltiples carritos y sin selectedEventoId
          setSelectedCart(null);
          setCurrentView('list');
        }
      }
    } else {
      setSelectedCart(null);
      setCurrentView('list');
    }
  }, [isOpen, carts, selectedEventoId]);

  const handleSelectCart = (cart) => {
    setSelectedCart(cart);
    setCurrentView('cart');
  };

  const handleBackToList = () => {
    setSelectedCart(null);
    setCurrentView('list');
  };

  const renderContent = () => {
    if (carts.length === 0) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-white">
            <p className="text-xl font-semibold mb-2">No cuentas con carritos activos</p>
            <p className="text-gray-400">Agrega tickets de algun evento para comenzar</p>
          </div>
        </div>
      );
    }

    if (currentView === 'list' && carts.length > 1) {
      return <CartsList carts={carts} onSelectCart={handleSelectCart} />;
    }

    if (currentView === 'cart' && selectedCart) {
      console.log("Selected Cart:", selectedCart);
      return (
        <>
          <CartTickets 
            tickets={selectedCart.tickets} 
            eventoInfo={{
              id: selectedCart.eventoId,
              eventoUid: selectedCart.eventoUid,
              nombre: selectedCart.eventoNombre,
              img: selectedCart.eventoImg,
              fecha: selectedCart.eventoFecha,
              ubicacion: selectedCart.eventoUbicacion
            }}
            carritoUid={selectedCart.carritoUid}
            onBack={carts.length > 1 ? handleBackToList : null}
            isUpdatingCart={isUpdatingCart}
            setIsUpdatingCart={setIsUpdatingCart}
          />
          <CartSummary 
            tickets={selectedCart.tickets}
            carritoUid={selectedCart.carritoUid}
            eventoUid={selectedCart.eventoUid}
            onOpenPayment={onOpenPayment}
            onCloseCart={onClose}
            showError={showError}
            isUpdatingCart={isUpdatingCart}
          />
        </>
      );
    }

    return null;
  };
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <Motion.div
          key="cart-modal-bg"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[85] bg-aw-bluedark sm:bg-aw-bluedark-50 flex items-start justify-end"
          onClick={onClose}
        >
          <Motion.div
            key="cart-modal"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="w-full max-w-md h-[92.5dvh] sm:h-full bg-aw-bluedark rounded-l-2xl shadow-2xl flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <CartHeader 
              onClose={onClose} 
              title={
                currentView === 'list' 
                  ? `Mis Carritos (${carts.length})`
                  : selectedCart 
                    ? selectedCart.eventoNombre 
                    : "Mi Carrito"
              }
              showBackButton={currentView === 'cart' && carts.length > 1}
              onBack={handleBackToList}
            />
            <div className="overflow-y-auto px-6 pt-2 pb-6 justify-between flex flex-col flex-1">
              {renderContent()}
            </div>
          </Motion.div>
        </Motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}